export interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
  group: string | null;
  changedAt: number;
}

export type HistoryAction<T> =
  | {
      type: 'update';
      update: (previous: T) => T;
      record: boolean;
      group: string | null;
      now: number;
    }
  | { type: 'undo' }
  | { type: 'redo' };

export const createHistory = <T>(present: T): HistoryState<T> => ({
  past: [],
  present,
  future: [],
  group: null,
  changedAt: 0,
});

export function reduceHistory<T>(
  state: HistoryState<T>,
  action: HistoryAction<T>,
): HistoryState<T> {
  if (action.type === 'undo') {
    if (!state.past.length) return state;
    return {
      past: state.past.slice(0, -1),
      present: state.past[state.past.length - 1]!,
      future: [state.present, ...state.future],
      group: null,
      changedAt: 0,
    };
  }
  if (action.type === 'redo') {
    if (!state.future.length) return state;
    return {
      past: [...state.past, state.present],
      present: state.future[0]!,
      future: state.future.slice(1),
      group: null,
      changedAt: 0,
    };
  }
  const present = action.update(state.present);
  if (present === state.present || JSON.stringify(present) === JSON.stringify(state.present))
    return state;
  if (!action.record)
    return {
      ...state,
      present,
      past: state.past.map(action.update),
      future: state.future.map(action.update),
      group: null,
    };
  const coalesce =
    action.group !== null && action.group === state.group && action.now - state.changedAt < 450;
  return {
    past: coalesce ? state.past : [...state.past.slice(-59), state.present],
    present,
    future: [],
    group: action.group,
    changedAt: action.now,
  };
}
